let CURRENT_DATE;
let timezoneOffsetFormat;
let timeFormat;
let dateTimeFormat;
let fullDateTimeFormat;
let dateFormat;
init();
/**
 * As a performance optimization, we only initialize some objects once. However to make testing
 * easier we expose a function that resets them.
 */
export function init() {
    CURRENT_DATE = new Date();
    /**
     * To convert to and from the account timezone, we need to know the offset for any given timestamp
     * (which may change with daylight saving time) rather than just the current offset. To determine
     * this, we format a date using `timeZoneName: longOffset` and parse the resulting string.
     */
    timezoneOffsetFormat = new Intl.DateTimeFormat("en-US", {
        timeZone: window.SPEEDCURVE_CONFIG.accountTimezone,
        timeZoneName: "longOffset",
    });
    timeFormat = new Intl.DateTimeFormat("en", {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
        timeZone: window.SPEEDCURVE_CONFIG.accountTimezone,
    });
    dateTimeFormat = new Intl.DateTimeFormat("en", {
        minute: "2-digit",
        hour: "numeric",
        hour12: true,
        day: "numeric",
        month: "short",
        timeZone: window.SPEEDCURVE_CONFIG.accountTimezone,
    });
    fullDateTimeFormat = new Intl.DateTimeFormat("en", {
        minute: "2-digit",
        hour: "numeric",
        hour12: true,
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: window.SPEEDCURVE_CONFIG.accountTimezone,
    });
    dateFormat = new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
        timeZone: window.SPEEDCURVE_CONFIG.accountTimezone,
    });
}
/**
 * Return a Unix timestamp for a given number of seconds ago
 */
export function secondsAgo(seconds) {
    return Math.floor(Date.now() / 1000) - seconds;
}
/**
 * Convert a Unix timestamp to a human-readable date string. If the timestamp is for the current
 * day, then only the time portion of the string will be returned (unless fullDate is set to true).
 * For previous and future days, the full datetime string will be returned.
 */
export function timestampToDateString(timestamp, fullDate = false) {
    const today = CURRENT_DATE;
    const date = new Date(timestamp * 1000);
    if (!fullDate && date.toLocaleDateString() === today.toLocaleDateString()) {
        // Just show the time when the date is the current day
        return timeString(date);
    }
    else if (!fullDate && date.getFullYear() !== today.getFullYear()) {
        //Date is not from this year so make sure we include the year in the format returned.
        return fullDateTimeString(date);
    }
    // Otherwise show the date string without year
    return dateTimeString(date);
}
/**
 * Return the offset in seconds for a given date in the account timezone.
 */
function getOffsetForDate(date) {
    // In case of any errors, fall back to the current account timezone offset.
    const fallbackOffset = window.SPEEDCURVE_CONFIG.accountTimezoneOffset * 60;
    const formatted = timezoneOffsetFormat.format(date);
    const [, offset] = formatted.split("GMT");
    const [hours, minutes] = offset.split(":").map(Number);
    if (isNaN(hours)) {
        return fallbackOffset;
    }
    if (isNaN(minutes)) {
        // The offset is only given in hours.
        return hours * 60 * 60;
    }
    // The offset has both an hour and minute component.
    // If the hours are negative, we need to negate the minutes as well.
    if (hours < 0) {
        return (hours * 60 - minutes) * 60;
    }
    return (hours * 60 + minutes) * 60;
}
export function fromAccountTimezone(timestamp) {
    const date = new Date(secondsToMilliseconds(timestamp));
    const offset = getOffsetForDate(date);
    return timestamp + offset;
}
export function toAccountTimezone(timestamp) {
    const date = new Date(secondsToMilliseconds(timestamp));
    const offset = getOffsetForDate(date);
    return timestamp - offset;
}
/**
 * Convert a Unix timestamp to an ISO 8601 string that can be used in <input type="date"> or
 * <input type="datetime-local">. Note that ISO 8601 is always in UTC, so the timestamp may need
 * to be adjusted before calling this function.
 */
export function timestampToDateInputString(timestamp) {
    return new Date(secondsToMilliseconds(timestamp)).toISOString().split("Z")[0];
}
/**
 * Append the account timezone abbreviation to a string.
 */
export function appendTimezone(str) {
    return `${str} (${window.SPEEDCURVE_CONFIG.accountTimezoneAbbreviation})`;
}
/**
 * Convert two Unix timestamps to a string representing the range between the timestamps. If the
 * timestamps are on the same day, the string will be formatted as "<timestamp1 datetime> - <timestamp2 time>".
 * Otherwise the string will be formatted as "<timestamp1 datetime> - <timestamp2 datetime>"
 */
export function timestampRangeString(timestamp1, timestamp2) {
    const date1 = new Date(timestamp1 * 1000);
    const date2 = new Date(timestamp2 * 1000);
    if (date1.toLocaleDateString() === date2.toLocaleDateString()) {
        // Both dates are the same day, so only show the time for the second date
        return `${dateTimeString(date1)} - ${timeString(date2)}`;
    }
    if (date1.getFullYear() !== CURRENT_DATE.getFullYear() || date2.getFullYear() !== CURRENT_DATE.getFullYear()) {
        // One or both of the years are not the current year, so show the full date and time for both
        return `${fullDateTimeString(date1)} - ${fullDateTimeString(date2)}`;
    }
    // Otherwise show the date and time for both
    return `${dateTimeString(date1)} - ${dateTimeString(date2)}`;
}
export function timeString(date) {
    return timeFormat.format(date);
}
export function dateTimeString(date) {
    return dateTimeFormat.format(date);
}
export function fullDateTimeString(date) {
    return fullDateTimeFormat.format(date);
}
export function dateString(date) {
    return dateFormat.format(date);
}
export function millisecondsToSeconds(ms) {
    return Math.round(ms / 1000);
}
export function secondsToMilliseconds(s) {
    return s * 1000;
}
export function timeAgo(date) {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) {
        return interval + " years ago";
    }
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) {
        return interval + " months ago";
    }
    interval = Math.floor(seconds / 86400);
    if (interval > 1) {
        return interval + " days ago";
    }
    interval = Math.floor(seconds / 3600);
    if (interval > 1) {
        return interval + " hours ago";
    }
    interval = Math.floor(seconds / 60);
    if (interval > 1) {
        return interval + " minutes ago";
    }
    if (seconds < 10)
        return "just now";
    return Math.floor(seconds) + " seconds ago";
}
export function trimSeconds(timestamp) {
    // Round down to the nearest minute
    return Math.floor(timestamp / 60) * 60;
}
/**
 * Calculate the bucket size for a date range.
 *
 * This function was copied from the \SpeedCurve\DateRange implementation in PHP.
 *
 * @link https://github.com/embrace-io/speedcurve-common/blob/main/RumDateRange.php
 */
export function getBucketSize(startTimestamp, endTimestamp) {
    const durationInSeconds = endTimestamp - startTimestamp;
    const durationInDays = durationInSeconds / 24 / 60 / 60;
    if (durationInSeconds <= 2 * 60 * 60) {
        // 2 hours or less = 1 minute bucket size
        return 60;
    }
    else if (durationInSeconds < 6 * 60 * 60) {
        // 2-6 hours = 2 minute bucket size
        return 2 * 60;
    }
    else if (durationInSeconds < 12 * 60 * 60) {
        // 6-12 hours = 5 minute bucket size
        return 5 * 60;
    }
    else if (durationInDays <= 1) {
        // 12-24 hours = 10 minute bucket size
        return 10 * 60;
    }
    else if (durationInDays <= 3) {
        // 1-3 days = 1 hour bucket size
        return 60 * 60;
    }
    else if (durationInDays <= 7) {
        // 3-7 days = 3 hour bucket size
        return 3 * 60 * 60;
    }
    else if (durationInDays <= 14) {
        // 7-14 days = 6 hour bucket size
        return 6 * 60 * 60;
    }
    else if (durationInDays <= 30) {
        // 14-30 days = 12 hour bucket size
        return 12 * 60 * 60;
    }
    else if (durationInDays <= 90) {
        // 30-90 days = 1 day bucket size
        return 24 * 60 * 60;
    }
    // More than 90 days = 7 day bucket size
    return 7 * 24 * 60 * 60;
}
